<script setup>
import { computed, ref } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const isRTL = useMapGetter('accounts/isRTL');

const activeTab = computed(() => {
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_agent_assist_open: isAgentAssistOpen,
  } = uiSettings.value;

  if (isContactSidebarOpen) return 0;
  if (isAgentAssistOpen) return 1;
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && (uiSettings.value?.is_contact_sidebar_open || uiSettings.value?.is_agent_assist_open)) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_agent_assist_open: false,
    });
  }
};

// Resizable width -- the panel (contact info / agent assist) defaults to the existing
// Tailwind-driven width (320px, 360px at 2xl) until the agent drags the handle at least
// once; from then on the chosen width is persisted per-agent via uiSettings, same
// mechanism already used for is_agent_assist_open above. Desktop (md:+) only -- mobile
// keeps its existing full-screen drawer layout untouched.
//
// The agent-assist tab's content is an <iframe> covering nearly the whole panel. Mouse/
// touch events over an iframe are dispatched to the IFRAME's own document, not the
// parent's -- they never bubble out, even same-origin. Plain mousemove/mouseup listeners
// on `document` would stop firing the instant the cursor crosses into the panel while
// dragging (i.e. almost immediately, since dragging INTO the panel is the whole point of
// widening it), leaving the drag "stuck" and, worse, leaving isResizing (and the
// col-resize/no-select styles on <body>) permanently stuck on since the terminating
// mouseup never arrives either. Pointer Events + setPointerCapture solves this correctly:
// once the handle captures the pointer, ALL subsequent pointer events for it are routed
// to the capturing element regardless of what's visually underneath, bypassing normal hit
// testing (including iframes) until pointerup/pointercancel releases it.
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 720;
const panelWidth = ref(uiSettings.value.conversation_sidebar_width || null);
const isResizing = ref(false);
const panelRef = ref(null);
let dragStartX = 0;
let dragStartWidth = 0;

const onResizeStart = event => {
  if (isSmallScreen.value) return;
  event.preventDefault();
  isResizing.value = true;
  dragStartX = event.clientX;
  dragStartWidth = panelWidth.value || panelRef.value?.getBoundingClientRect().width || MIN_PANEL_WIDTH;
  event.target.setPointerCapture(event.pointerId);
  Object.assign(document.body.style, { cursor: 'col-resize', userSelect: 'none' });
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  // The panel sits on the inline-end side (right in LTR); dragging the handle towards the
  // start of the page (left in LTR) should widen it -- the opposite sign from a start-side
  // sidebar's resize handle.
  const delta = isRTL.value
    ? event.clientX - dragStartX
    : dragStartX - event.clientX;
  panelWidth.value = Math.min(
    MAX_PANEL_WIDTH,
    Math.max(MIN_PANEL_WIDTH, dragStartWidth + delta)
  );
};

const onResizeEnd = event => {
  if (!isResizing.value) return;
  isResizing.value = false;
  if (event?.target?.hasPointerCapture?.(event.pointerId)) {
    event.target.releasePointerCapture(event.pointerId);
  }
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  updateUISettings({ conversation_sidebar_width: panelWidth.value });
};

useEventListener(document, 'pointermove', onResizeMove);
useEventListener(document, 'pointerup', onResizeEnd);
useEventListener(document, 'pointercancel', onResizeEnd);

const panelStyle = computed(() => {
  if (isSmallScreen.value || !panelWidth.value) return {};
  return {
    width: `${panelWidth.value}px`,
    minWidth: `${panelWidth.value}px`,
    maxWidth: `${panelWidth.value}px`,
  };
});
</script>

<template>
  <div
    ref="panelRef"
    v-on-click-outside="() => closeContactPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:relative md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab !== null,
        'md:hidden': activeTab === null,
      },
    ]"
    :style="panelStyle"
  >
    <!-- Resize Handle (desktop only) -->
    <div
      v-if="activeTab !== null"
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:left-0 rtl:right-0 group"
      style="touch-action: none;"
      title="Drag to resize"
      @pointerdown="onResizeStart"
    >
      <div
        class="absolute top-0 h-full w-px ltr:left-0 rtl:right-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <iframe
        v-if="activeTab === 1"
        :src="`/agent-assist/?conversation_id=${currentChat.id}`"
        style="width:100%;height:100%;border:none;"
      />
    </div>
  </div>
</template>
